import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import router from '@/router';
import { useModalStore } from '@/stores/modal';
import { useToast } from 'vue-toastification';

axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
axios.defaults.baseURL = process.env.VUE_APP_API_ENDPOINT;

const maintenanceCodes = new Set([
    'PENSION_CREDITS_MAINTENANCE',
    'SUBMISSIONS_MAINTENANCE',
]);

const maintenanceCooldown = 5000;
let lastMaintenanceToast = 0;

axios.interceptors.request.use(
    (config) => {
        const authStore = useAuthStore();
        const { token, impersonationToken } = authStore;

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (impersonationToken) {
            config.headers['X-Impersonation-Token'] = `${impersonationToken}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const authStore = useAuthStore();

        if (error.response) {
            const { status, data } = error.response;

            switch (status) {
                case 401 :
                    // Handle 401 Unauthorized
                    authStore.logout();
                    router.push({ name: 'login' });
                    useModalStore().closeAllModals();
                break;

                // Forbidden
                case 403 :
                    useModalStore().closeAllModals();
                    // Handle 403 Forbidden
                    if (data.code === '2FA_REQUIRED') {
                        router.push({ name: '2fa.verify' });
                    } else if (data.code === '2FA_SETUP_REQUIRED') {
                        router.push({ name: '2fa.setup' });
                    } else {
                        useToast().error(
                            data?.error ?? data?.message ?? 'You are not authorized to perform this action.'
                        );
                    }
                break;

                case 423 :
                    // Handle 423 Locked
                    router.push({ name: 'UserLocked' });
                    useModalStore().closeAllModals();
                break;
    
                case 503 : {
                    const code = typeof data?.code === 'string' ? data.code.trim() : null;

                    if (code && maintenanceCodes.has(code)) {
                        const now = Date.now();

                        if (now - lastMaintenanceToast >= maintenanceCooldown) {
                            lastMaintenanceToast = now;

                            useToast().error(
                                data?.message ?? data?.error ?? 'System under maintenance, please try again later.'
                            );
                        }
                    }
                }
                break;

                default:
                    //
                    break;
            }
        }

        return Promise.reject(error);
    }
);

export default axios;